import * as T from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { clone } from 'three/addons/utils/SkeletonUtils.js';
import { WILDLIFE, wildlifeFrame, wildlifeGround, type WildlifeMsg, type WildlifeHerdView, type WildlifeMemberView } from '@hyperlight/shared';
import type { StarSystem } from '../world/starsystem';
import { ForestDeer } from './forestDeer';
interface Actor {deer:ForestDeer;last:WildlifeMemberView;time:number;posedAt:number}
interface Herd {view:WildlifeHerdView;root:T.Group;height:(x:number,z:number)=>number;actors:Map<string,Actor>}
const lerp=T.MathUtils.lerp;
/** Planet-local snapshots are interpolated before posing. The rig/IK solver
 * runs in a fixed habitat frame, so planet spin and floating-origin rebases
 * cannot drag a planted hoof or change the animal's gait speed. */
export class WorldWildlife {
  private snapshots:WildlifeMsg[]=[];
  private herds=new Map<string,Herd>();
  private asset:GLTF|null=null;private loading=false;private failedAt=0;
  private receivedAt=0;
  constructor(private scene:T.Scene){}
  snapshot(msg:WildlifeMsg){
    if(msg.time<=(this.snapshots.at(-1)?.time??-Infinity))return;
    this.snapshots.push(msg);if(this.snapshots.length>20)this.snapshots.shift();this.receivedAt=performance.now();
  }
  private load(){
    if(this.asset||this.loading||performance.now()-this.failedAt<5000)return;
    this.loading=true;
    void new GLTFLoader().loadAsync('/models/creatures/forest-deer.glb').then(asset=>{this.asset=asset;})
      .catch(error=>{this.failedAt=performance.now();console.warn('[wildlife] model load failed',error);}).finally(()=>{this.loading=false;});
  }
  clear(){for(const h of this.herds.values()){for(const a of h.actors.values())a.deer.dispose();h.root.removeFromParent();}this.herds.clear();this.snapshots=[];}
  update(dt:number,_origin:T.Vector3,camera:T.Vector3,system:StarSystem,timeMs:number):void {
    const latest=this.snapshots.at(-1);if(!latest)return;
    // A dropped connection freezes presentation instead of inventing behavior.
    const stale=performance.now()-this.receivedAt>1500;
    let a=this.snapshots[0],b=a;
    for(const snap of this.snapshots){if(snap.time<=timeMs)a=snap;else {b=snap;break;}b=snap;}
    const k=b.time>a.time?T.MathUtils.clamp((timeMs-a.time)/(b.time-a.time),0,1):1;
    const live=new Set<string>();
    let builds=0;
    for(const view of b.herds) {
      const planet=system.planets[view.planet];if(!planet)continue;
      const at=new T.Vector3(...view.origin).applyQuaternion(planet.group.quaternion).add(planet.group.position);
      if(at.distanceTo(camera)>WILDLIFE.renderM)continue;
      live.add(view.id);this.load();if(!this.asset)continue;
      let herd=this.herds.get(view.id);
      if(!herd){
        const frame=wildlifeFrame(view.origin),root=new T.Group(),cache=new Map<string,number>();
        const grid=(x:number,z:number)=>{const key=`${x},${z}`;let y=cache.get(key);if(y===undefined){y=wildlifeGround(planet.sampler,frame,x*.25,z*.25).y;if(cache.size>=4096)cache.delete(cache.keys().next().value!);cache.set(key,y);}return y;};
        const height=(x:number,z:number)=>{const gx=x*4,gz=z*4,ix=Math.floor(gx),iz=Math.floor(gz);return lerp(lerp(grid(ix,iz),grid(ix+1,iz),gx-ix),lerp(grid(ix,iz+1),grid(ix+1,iz+1),gx-ix),gz-iz);};
        herd={view,root,height,actors:new Map()};this.scene.add(root);this.herds.set(view.id,herd);
        root.name='Persistent forest deer';
      }
      herd.view=view;herd.root.position.copy(at);
      const frame=wildlifeFrame(view.origin),basis=new T.Matrix4().makeBasis(new T.Vector3(...frame.right),new T.Vector3(...frame.up),new T.Vector3(...frame.forward));
      herd.root.quaternion.copy(planet.group.quaternion).multiply(new T.Quaternion().setFromRotationMatrix(basis));
      const old=a.herds.find(h=>h.id===view.id);
      for(const next of view.members){
        let actor=herd.actors.get(next.id);
        if(!actor){
          if(builds++>=1)continue;
          const deer=new ForestDeer(clone(this.asset.scene) as T.Group,this.asset.animations,{radius:1000,obstacles:[]},herd.height,false);
          deer.grounding.reset();actor={deer,last:next,time:0,posedAt:timeMs};herd.actors.set(next.id,actor);
        }
        const prev=old?.members.find(m=>m.id===next.id)??next;
        const x=lerp(prev.x,next.x,k),z=lerp(prev.z,next.z,k),yaw=prev.yaw+Math.atan2(Math.sin(next.yaw-prev.yaw),Math.cos(next.yaw-prev.yaw))*k;
        const state=(k<.5?prev:next),deer=actor.deer;
        const elapsed=Math.max(0,(timeMs-state.since)/1000);
        // Detachment makes the existing ground solver's world frame exactly
        // this persistent habitat frame; the parent is restored before render.
        const gap=(timeMs-actor.posedAt)/1000;
        const replant=gap>.25||Math.hypot(x-deer.group.position.x,z-deer.group.position.z)>1;
        const look=state.look,dist=look?Math.hypot(look[0]-x,look[2]-z):Infinity;
        const tier=at.distanceTo(camera)<55?1:3;actor.time+=dt;
        if(actor.time>=tier/60||!deer.group.parent){
          deer.group.removeFromParent();
          if(replant)deer.grounding.reset();
          deer.updatePose(stale?0:Math.min(.1,Math.max(0,gap)),{x,z,yaw,speed:lerp(prev.speed,next.speed,k),state:state.activity==='drink'?'graze':state.activity,
            elapsed,fear:state.fear,distance:dist,time:timeMs/1000,lookAtPlayer:!!look,scale:1.15},
            {x:look?.[0]??x,z:look?.[2]??z,speed:0,visible:!!look});
          actor.time=0;actor.posedAt=timeMs;
        }
        herd.root.add(deer.group);actor.last={...state,x,z,yaw};
      }
    }
    for(const [id,h] of this.herds)if(!live.has(id)){for(const a of h.actors.values())a.deer.dispose();h.root.removeFromParent();this.herds.delete(id);}
  }
  debug(){return {herds:[...this.herds.values()].map(h=>({id:h.view.id,planet:h.view.planet,origin:h.view.origin,members:[...h.actors.values()].map(a=>({...a.last,clip:a.deer.clip,grounding:a.deer.grounding.snapshot()}))})),snapshots:this.snapshots.length};}
}
